import { DAILY_REWARDS } from '../balance.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import { premiumOf } from '../premium.js';
import type { BotUser } from '../state.js';

/** Midnight (server time, parity P-4) of `ms` + `days`. */
function midnightAfter(ms: number, days: number): number {
  const d = new Date(ms);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export type DailyStatus = { state: 'available'; streakBroken: boolean } | { state: 'claimed'; nextAt: number };

/** `rpg/daily.ts#fillReward` gate: once per day; a missed day resets the streak. */
export function dailyStatus(save: BotUser, now: number): DailyStatus {
  if (save.daily.date === 0) return { state: 'available', streakBroken: false };
  const limit = midnightAfter(save.daily.date, 2);
  const takeReward = midnightAfter(save.daily.date, 1);
  if (now > limit) return { state: 'available', streakBroken: true };
  if (now > takeReward) return { state: 'available', streakBroken: false };
  return { state: 'claimed', nextAt: takeReward };
}

/** Streak multiplier `1 + trunc(inARow/15)/10` (normal-claim branch only). */
export function streakRatio(inARow: number): number {
  return 1 + Math.trunc(inARow / 15) / 10;
}

/** `fillReward`: premium adds `reward.fakecoin` % to the money and `reward.lootcoin` lootcoins. */
export function claimDaily(save: BotUser, now: number, events: GameEvent[]): void {
  const status = dailyStatus(save, now);
  if (status.state === 'claimed') throw new GameError('dailyAlreadyClaimed', { nextAt: status.nextAt });
  if (save.daily.day >= DAILY_REWARDS.length) save.daily.day = 0;

  const premium = premiumOf(save, now);
  const premFakecoin = premium.premium > 0 ? 1 + premium.reward.fakecoin / 100 : 1;
  const premLootcoin = premium.premium > 0 ? premium.reward.lootcoin : 0;

  if (status.streakBroken) save.daily.day = 0;
  const reward = DAILY_REWARDS[save.daily.day];
  if (!reward) return;
  let money: number;
  if (status.streakBroken) {
    money = Math.trunc(reward.money * premFakecoin);
    save.stats.reward.maxInARow = Math.max(save.stats.reward.inARow, save.stats.reward.maxInARow);
    save.stats.reward.inARow = 1;
  } else {
    money = Math.trunc(reward.money * premFakecoin * streakRatio(save.stats.reward.inARow));
    save.stats.reward.inARow += 1;
    save.stats.reward.maxInARow = Math.max(save.stats.reward.inARow, save.stats.reward.maxInARow);
  }
  const lootbox = reward.lootbox + premLootcoin;
  save.daily.date = now;
  save.money += money;
  save.stats.money.total += money;
  save.lootbox += lootbox;
  save.shopcoin += reward.shopcoin;
  save.stats.reward.count += 1;
  save.daily.day += 1;
  events.push({ type: 'dailyClaimed', day: save.daily.day, money, lootbox, shopcoin: reward.shopcoin, streakBroken: status.streakBroken });
}
